import React, { useEffect, useState } from "react"
import { Box, Button, Checkbox, FormControl, FormControlLabel, Radio, RadioGroup } from "@material-ui/core"
import { Skeleton } from "@material-ui/lab"
import html2canvas from "html2canvas"
import jsPDF from "jspdf"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import ExportEventTable from "./ExportEventTable"
import { ModalPopup } from "../modal"
import ExportFileName from "./ExportFileName"
import { setNotification } from "../../actions/notificationActions"
import useStyles from "./ExportEventStyle"

const EXPORT_AS = ["pdf", "img"]
const DOMID = {
	chart: {
		id: "export-chart",
		name: "Chart",
	},
	list: {
		id: "eventList",
		name: "Events",
	},
}

const ExportEvent = ({ callback }) => {
	const [t] = useTranslation("global")
	const classes = useStyles()
	const dispatch = useDispatch()

	const events = useSelector((state) => state.events.selectedEvents)

	const [chartImage, setChartImage] = useState("")
	const [exportAs, setExportAs] = useState(EXPORT_AS[0])
	const [isLoading, setIsLoading] = useState(false)
	const [exporting, setExporting] = useState(false)
	const [page1, setPage1] = useState(true)
	const [page2, setPage2] = useState(events?.length ? true : false)
	const [fileName, setfileName] = useState("")
	const [openExportFileName, setOpenExportFileName] = useState(false)

	const isValid = !(page1 || page2)

	const getImageFromDOM = async (DOMId) => {
		const chart = document.getElementById(DOMId)
		const canvas = await html2canvas(chart, { logging: true, letterRendering: 1, useCORS: true })
		const img = canvas.toDataURL("image/png")
		return {
			image: img,
			height: canvas.height,
			width: canvas.width,
		}
	}

	useEffect(() => {
		const captureChartImage = async () => {
			try {
				setIsLoading(true)
				const data = await getImageFromDOM(DOMID.chart.id)
				setChartImage(data)
			} catch (error) {
				console.log("Error in html2canvas: ", error)
			} finally {
				setIsLoading(false)
			}
		}
		captureChartImage()
	}, [])

	const exportFile = async () => {
		try {
			setExporting(true)
			const docExport = []

			if (page1) {
				docExport.push({ ...chartImage, width: 420, height: 250, marginLeft: 10, marginTop: 20 })
			}

			if (page2) {
				const data = await getImageFromDOM(DOMID.list.id)
				docExport.push({
					...data,
					height: (data.height * 400) / data.width,
					width: 400,
					marginLeft: 10,
					marginTop: 20,
				})
			}

			if (exportAs === EXPORT_AS[0]) {
				//if export as PDF download 1 pdf with multiple pages
				const pdf = new jsPDF("p", "px", "a4")
				const pageHeight = 800 // A4 size paper height in pixels
				let position = 0

				docExport.forEach((doc, i) => {
					//if (i > 0) pdf.addPage() //if more than 1 images then add new page
					if (i === 0) {
						pdf.addImage(doc?.image, "PNG", doc.marginLeft, doc.marginTop, doc.width, doc.height)
					} else {
						// pdf.addImage(doc?.image, "PNG", doc.marginLeft, doc.marginTop, doc.width, (doc.height * doc.width) / doc.width)
						// if (doc.height > pageHeight) {
						// 	let remainingHeight = doc.height - pageHeight

						// 	while (remainingHeight > 0) {
						// 		pdf.addPage()
						// 		position -= pageHeight
						// 		pdf.addImage(
						// 			doc?.image,
						// 			"PNG",
						// 			doc.marginLeft,
						// 			position + 100,
						// 			doc.width,
						// 			(doc.height * doc.width) / doc.width
						// 		)
						// 		remainingHeight -= pageHeight
						// 	}
						// }

						const imageHeight = doc.height
						const pageHeight = 840
						const imagePerPage = Math.ceil(imageHeight / 630)
						pdf.addPage()
						for (let index = 0; index < imagePerPage; index++) {
							pdf.addImage(
								doc?.image,
								"PNG",
								doc.marginLeft,
								index === 0
									? 0
									: index * pageHeight - 210 * index > imageHeight
									? imageHeight
									: -(index * pageHeight - 210 * index),
								//index === 0 ? 0 : -630,
								doc.width,
								doc.height
							)
							if (imagePerPage !== index + 1) pdf.addPage()
						}
					}
				})
				pdf.save(`${fileName}.pdf`)
			} else {
				//if export as image download multiple images
				docExport.forEach((doc, i) => {
					const link = document.createElement("a")
					link.href = doc?.image
					link.download = `${fileName}-${i + 1}.png`
					link.click() // Trigger the download
					link.remove() // Remove the link element from the DOM
				})
			}
			dispatch(
				setNotification({
					data: t("event.docExported"),
					severity: "success",
					duration: 3000,
				})
			)
		} catch (error) {
			console.log("Error in exporting to PDF: ", error)
			setNotification({
				data: t("event.somethingWentWrong"),
				severity: "error",
				duration: 3000,
			})
		} finally {
			setExporting(false)
			setfileName("")
			setOpenExportFileName(false)
		}
	}

	return (
		<Box className={classes.root}>
			<div className={classes.dateWrapper}>
				<p className={classes.preview}>{t("event.preview")}</p>
			</div>
			{isLoading ? (
				<Skeleton variant="rect" height={200} />
			) : (
				<div className={`${classes.printWrapper} ${page1 && classes.printInclude}`}>
					<FormControlLabel
						control={
							<Checkbox
								size="small"
								checked={page1}
								onChange={(e) => setPage1(e.target.checked)}
								name="page1"
								color="primary"
							/>
						}
						label={t("event.page1")}
					/>
					<img src={chartImage?.image} alt="chart" id="eventChartImg" className={classes.eventChartImg} />
				</div>
			)}
			{events?.length > 0 && (
				<div className={`${classes.printWrapper} ${page2 && classes.printInclude}`} style={{ marginTop: 24 }}>
					<FormControlLabel
						control={
							<Checkbox
								size="small"
								checked={page2}
								onChange={(e) => setPage2(e.target.checked)}
								name="page1"
								color="primary"
							/>
						}
						label={t("event.page2")}
					/>
					<div id="eventList">
						<ExportEventTable isLoading={isLoading} />
					</div>
				</div>
			)}

			<Box className={classes.exportOptions}>
				<FormControl component="fieldset">
					<RadioGroup
						aria-label="export"
						name="export1"
						value={exportAs}
						row
						onChange={(event) => {
							setExportAs(event.target.value)
						}}
					>
						<FormControlLabel value={EXPORT_AS[0]} control={<Radio size="small" />} label={t("event.exportPDF")} />
						<FormControlLabel value={EXPORT_AS[1]} control={<Radio size="small" />} label={t("event.exportImg")} />
					</RadioGroup>
				</FormControl>
			</Box>

			<hr className={classes.dividerLine} />

			<FormControl className={classes.formActions}>
				<Button
					variant="contained"
					color="primary"
					className={`${classes.formActionsBtn} ${classes.addBtn}`}
					type="submit"
					disabled={exporting || isLoading || isValid}
					onClick={() => setOpenExportFileName(true)}
				>
					{exporting ? t("event.exporting") : t("event.export")}
				</Button>
				<Button
					className={classes.formActionsBtn}
					style={{
						textTransform: "initial",
						fontSize: "14px",
						padding: "8px 12px",
					}}
					onClick={() => callback(false)}
				>
					{t("event.field.cancel")}
				</Button>
			</FormControl>
			<ModalPopup
				title={exportAs === "pdf" ? t("event.exportPDF") : t("event.exportImg")}
				open={openExportFileName}
				setOpen={setOpenExportFileName}
			>
				<ExportFileName
					download={exportFile}
					exportAs={exportAs}
					classes={classes}
					exporting={exporting}
					setOpenExportFileName={setOpenExportFileName}
					fileName={fileName}
					setfileName={setfileName}
				/>
			</ModalPopup>
		</Box>
	)
}

export default ExportEvent
